import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useBoolean } from 'react-use';
import * as Sentry from '@sentry/react';

import { useWebViewBridge } from 'lib/web-view-bridge';
import { WebViewBridge } from 'common/webviewBridge';
import { setCredential } from 'common/auth/credential';

const CREDENTIALS_KEY = '__classting_app.credentials';

const useAuth = () => {
  const auth = useOidcAuth();
  const location = useLocation();
  const {
    isClasstingRNWebView,
    setAuthenticated: setWebviewAuthenticated,
    isAuthenticated: isWebViewAuthenticated,
  } = useWebViewBridge();
  const [isAuthenticated, setAuthenticated] = useBoolean(
    (isClasstingRNWebView() && isWebViewAuthenticated) ||
      (auth.isAuthenticated && !!auth.user)
  );
  const [error, setError] = useState(auth.error);

  const from = location.pathname + location.search + location.hash;

  useEffect(() => {
    if (
      (isClasstingRNWebView() && isWebViewAuthenticated) ||
      (auth.isAuthenticated && auth.user && !auth.error)
    ) {
      setAuthenticated(true);
    }
  }, [
    auth.error,
    auth.isAuthenticated,
    auth.user,
    isClasstingRNWebView,
    isWebViewAuthenticated,
    setAuthenticated,
  ]);

  useEffect(() => {
    if (auth.error) {
      setError(auth.error);
    }
  }, [auth.error]);

  const authorize = useCallback(async () => {
    if (isClasstingRNWebView()) {
      const credential = window.sessionStorage.getItem(CREDENTIALS_KEY);
      if (credential) {
        setCredential(JSON.parse(credential));

        setWebviewAuthenticated(true);
      } else {
        try {
          const { token_type, access_token } = await WebViewBridge.authorize();
          setCredential({
            token_type,
            access_token,
          });

          setWebviewAuthenticated(true);
        } catch (error) {
          // FIXME 임시로 하위 버전에서 앱 허용
          console.error(error);
          Sentry.captureException(error);

          window.setToken = (token: string) => {
            setCredential({
              token_type: 'Bearer',
              access_token: token,
            });

            setWebviewAuthenticated(true);
          };

          const msg = 'ctjscall://authorize?callback=setToken';

          if (window?.ctandroid !== undefined) {
            window?.ctandroid.submitFromWeb(msg);
          } else {
            window.location.href = msg;
          }
        }
      }

      return;
    }

    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated || !auth.user || auth.error) {
      window.sessionStorage.setItem('redirectUrl', from);
      auth.signinRedirect();
    }
  }, [auth, from, isClasstingRNWebView, setWebviewAuthenticated]);

  return { isAuthenticated, authorize, error };
};

export default useAuth;
